import React, { useEffect, useState } from "react";
import { IoMdArrowDropdown } from "react-icons/io";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import IndianFlag from "../../../assets/icons/India.webp";
import {
  useAppSelector,
  checkUserNameExists,
  sendOTP,
  useAppDispatch,
  getOtpButtonVisibility,
} from "@hypexone/uicore";
import { SVLS_API_CLIENT } from "../../../App.utils";
import { USERNAME_REGEX } from "../Payments/commonutils";
import Button from "../../components/shared/Button/Button";

interface StepOneProps {
  otp: number;
  otpSent: boolean;
  phoneNumber: string;
  agreedToTerms: boolean;
  username: string;
  email: string;
  password: string;
  showPassword: boolean;
  setOtp: Function;
  setPhoneNumber: any;
  onEmailChange: any;
  onPhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTermsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTermsClick?: () => void;
  onUsernameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showPasswordClickHandler: Function;
  onUsernameValidityChange: (isValid: boolean) => void;
  campaignCode: string;
  onCampaignCodeChange: (val: string) => void;
  referralCode: string;
}

const StepOne: React.FC<StepOneProps> = ({
  otp,
  otpSent,
  phoneNumber,
  agreedToTerms,
  setPhoneNumber,
  username,
  password,
  showPassword,
  setOtp,
  onEmailChange,
  onPhoneChange,
  email,
  onTermsChange,
  onTermsClick,
  onUsernameChange,
  onPasswordChange,
  showPasswordClickHandler,
  onUsernameValidityChange,
  campaignCode,
  onCampaignCodeChange,
  referralCode,
}) => {
  const dispatch = useAppDispatch();
  const { langData } = useAppSelector((state) => state.cms);
  const { domainConfig } = useAppSelector((state) => state.common);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [otpTimer, setOtpTimer] = useState<number>();
  const [usernameSeleted, setUsernameSelected] = useState<boolean>(false);
  const checkUserExists = async (user: string) => {
    setUsernameSelected(true);
    let valid = true;
    if (user?.length < 4) {
      setErrorMsg("Username must have at least 4 characters");
      return;
    } else if (!USERNAME_REGEX.test(user)) {
      setErrorMsg("Invalid username format");
      return;
    }
    let res = await checkUserNameExists(SVLS_API_CLIENT, user?.toLowerCase());
    if (res === true) {
      setErrorMsg("Username already exists");
      valid = false;
    } else {
      setErrorMsg("");
      valid = true;
    }
    onUsernameValidityChange(valid);
  };
  useEffect(() => {
    if (usernameSeleted) {
      checkUserExists(username);
    } else {
      onUsernameValidityChange(false);
    }
  }, [username]);
  useEffect(() => {
    if (otpSent) {
      handleOtpTimer(60);
    }
  }, [otpSent]);
  const handleOtpTimer = (time) => {
    if (time >= 0) {
      setOtpTimer(time);
      setTimeout(() => {
        handleOtpTimer(time - 1);
      }, 1000);
    } else {
      setTimeout(() => {
        setOtpTimer(undefined);
      }, 1000);
    }
  };

  const otpChannelBlocked =
    otpTimer !== undefined && otpTimer !== null && otpTimer >= 0;

  const { showSmsOtpButton, showWhatsappOtpButton, showGenericOtpButton } =
    getOtpButtonVisibility(domainConfig);

  const requestPhoneOtp = (isWhatsapp: boolean) => {
    if (otpChannelBlocked || !phoneNumber) return;
    dispatch(sendOTP({ phoneNumber: "91" + phoneNumber, isWhatsapp }));
    handleOtpTimer(60);
  };

  const otpBtnClass =
    "h-12 rounded-lg border-0 !bg-header-gradient px-3 text-[17px] font-bold text-goldenYellow shadow-[0_6px_16px_rgba(0,0,0,0.18)] transition-all duration-200 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="mt-1 flex gap-2">
          <div className="flex h-12 w-[78px] shrink-0 items-center rounded-xl border border-solid border-neutral-200 bg-[#fdf3d2] px-1.5 text-sm text-black shadow-sm">
            <img src={IndianFlag} alt="" className="h-4 w-4" />
            <div className="ml-0.5">+91</div>
            <IoMdArrowDropdown />
          </div>
          <input
            type="tel"
            id="phoneNumber"
            className="flex h-12 w-full items-center rounded-xl border border-solid border-neutral-200 bg-[#fdf3d2] px-3 py-2 text-sm text-black shadow-sm placeholder:text-gray-500 focus:border-black/30 focus:outline-none focus:ring-1 focus:ring-black/20"
            placeholder={langData?.["enter_phone_number_txt"]}
            value={phoneNumber}
            onChange={onPhoneChange}
          />
        </div>
        {(showSmsOtpButton || showWhatsappOtpButton || showGenericOtpButton) &&
          !otpSent && (
          <div className="mt-2 flex gap-2">
            {(showSmsOtpButton || showGenericOtpButton) && (
              <Button
                type="button"
                className={`${showWhatsappOtpButton ? "flex-1" : "w-full"} ${otpBtnClass}`}
                onClick={() => requestPhoneOtp(false)}
                disabled={
                  phoneNumber?.length < 9 || otpChannelBlocked || !!errorMsg
                }
              >
                {showGenericOtpButton
                  ? langData?.["send_otp"] ?? "Send OTP"
                  : langData?.["get_otp"] ?? "SMS OTP"}
              </Button>
            )}
            {showWhatsappOtpButton && (
              <Button
                type="button"
                className={`${showSmsOtpButton || showGenericOtpButton ? "flex-1" : "w-full"} ${otpBtnClass}`}
                onClick={() => requestPhoneOtp(true)}
                disabled={
                  phoneNumber?.length < 9 || otpChannelBlocked || !!errorMsg
                }
              >
                {langData?.["get_otp_whatsapp"] ?? "WhatsApp OTP"}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 self-start text-black4">
        <input
          type="checkbox"
          id="termsAgreement"
          className="h-4 w-4 shrink-0 rounded border-gray-300 bg-white text-black accent-black"
          checked={agreedToTerms}
          onChange={onTermsChange}
        />
        <label
          htmlFor="termsAgreement"
          className="flex-auto text-[11px] leading-4 text-black4"
        >
          {langData?.["agree_terms_prefix"] ?? "I agree to the "}
          {onTermsClick ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onTermsClick();
              }}
              className="cursor-pointer border-0 bg-transparent p-0 font-inherit font-semibold text-black"
            >
              {langData?.["terms_conditions"] ?? "Terms and Conditions"}
            </button>
          ) : (
            (langData?.["terms_conditions"] ?? "Terms and Conditions")
          )}
          .
        </label>
      </div>

      <div>
        <input
          type="text"
          id="username"
          className="flex h-12 w-full items-center rounded-xl border border-solid border-neutral-200 bg-[#fdf3d2] px-3 py-2 text-sm text-black shadow-sm placeholder:text-gray-500 focus:border-black/30 focus:outline-none focus:ring-1 focus:ring-black/20"
          placeholder={langData?.["enter_username_txt"]}
          value={username}
          onBlur={(e) => checkUserExists(e.target.value)}
          onChange={onUsernameChange}
        />
        {errorMsg && <div className="mt-0.5 text-xs text-red-500">{errorMsg}</div>}
      </div>

      <div>
        <div className="flex h-12 w-full items-center rounded-xl border border-solid border-neutral-200 bg-[#fdf3d2] px-3 py-2 text-black shadow-sm focus-within:border-black/30 focus-within:outline-none focus-within:ring-1 focus-within:ring-black/20">
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            className="w-full border-none bg-transparent text-sm outline-none placeholder:text-gray-500"
            placeholder={langData?.["enter_password"]}
            value={password}
            onChange={onPasswordChange}
          />
          <div
            className="shrink-0 cursor-pointer rounded-full p-1 text-black4 transition-colors hover:bg-black/5"
            onClick={() => showPasswordClickHandler()}
            role="button"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <FaEye /> : <FaEyeSlash />}
          </div>
        </div>
      </div>
      <div>
        <input
          type="text"
          id="campaignCode"
          className="flex h-12 w-full items-center rounded-xl border border-solid border-neutral-200 bg-[#fdf3d2] px-3 py-2 text-sm text-black shadow-sm placeholder:text-gray-500 focus:border-black/30 focus:outline-none focus:ring-1 focus:ring-black/20"
          placeholder={
            langData?.["enter_campaign_code"] ?? "Enter Campaign Code"
          }
          value={campaignCode}
          onChange={(e) => onCampaignCodeChange(e.target.value)}
        />
      </div>

      {referralCode && (
        <div>
          <label
            htmlFor="referralCode"
            className="self-start text-xs font-bold text-black4"
          >
            {langData?.["referral_code"] ?? "Referral Code"}
          </label>
          <input
            type="text"
            id="referralCode"
            readOnly
            className="mt-1 flex h-12 w-full items-center rounded-xl border border-solid border-neutral-200 bg-[#fdf3d2] px-3 py-2 text-sm text-gray-500 shadow-sm focus:outline-none"
            value={referralCode}
          />
        </div>
      )}

      {otpSent && (
        <div>
          <label htmlFor="otp" className="self-start text-xs font-bold text-black4">
            {langData?.["otp"]} *
          </label>
          <div className="mt-1 flex gap-2">
            <input
              type="tel"
              id="otp"
              className="h-12 w-full rounded-xl border border-solid border-neutral-200 bg-[#fdf3d2] px-3 py-2 text-sm text-black shadow-sm placeholder:text-gray-500 focus:border-black/30 focus:outline-none focus:ring-1 focus:ring-black/20"
              placeholder={langData?.["enter_otp_txt"]}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
          </div>
          {(showSmsOtpButton || showWhatsappOtpButton || showGenericOtpButton) && (
            <div className="mt-1.5">
              {otpChannelBlocked ? (
                <div className="text-xs text-black4">
                  {`${langData?.["resend_in_txt"]} ${otpTimer} ${langData?.["seconds"]}`}
                </div>
              ) : (
                <div className="flex gap-2">
                  {(showSmsOtpButton || showGenericOtpButton) && (
                    <Button
                      type="button"
                      className={`${showWhatsappOtpButton ? "flex-1" : "w-full"} ${otpBtnClass}`}
                      onClick={() => requestPhoneOtp(false)}
                      disabled={phoneNumber?.length < 9 || !!errorMsg}
                    >
                      {showGenericOtpButton
                        ? langData?.["send_otp"] ?? "Send OTP"
                        : langData?.["resend_sms_otp"] ?? "Resend SMS OTP"}
                    </Button>
                  )}
                  {showWhatsappOtpButton && (
                    <Button
                      type="button"
                      className={`${showSmsOtpButton || showGenericOtpButton ? "flex-1" : "w-full"} ${otpBtnClass}`}
                      onClick={() => requestPhoneOtp(true)}
                      disabled={phoneNumber?.length < 9 || !!errorMsg}
                    >
                      {langData?.["resend_whatsapp_otp"] ?? "Resend WhatsApp OTP"}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StepOne;
